











































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import "@fullcalendar/core/vdom";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import googleCalendarPlugin from "@fullcalendar/google-calendar";
import EventDialog from "./EventDialog.vue";

interface CalendarEvent {
  id?: string;
  title: string;
  start: Date | string;
  end?: Date | string;
  description?: string;
  color?: string;
  category?: string;
  allDay?: boolean;
}

@Component({
  name: "AdvancedCalendar",
  components: {
    FullCalendar,
    EventDialog,
  },
})
export default class AdvancedCalendar extends Vue {
  @Prop({ default: () => [] }) events!: CalendarEvent[];
  @Prop({ default: "Calendario" }) title!: string;
  @Prop({ default: true }) editable!: boolean;
  @Prop({ default: "" }) googleCalendarApiKey!: string;
  @Prop({ default: "" }) googleCalendarId!: string;
  @Prop({ default: true }) enableGoogleCalendar!: boolean;

  eventDialog = false;
  selectedEvent: CalendarEvent | null = null;
  localEvents: CalendarEvent[] = [];

  get calendarOptions() {
    const options: any = {
      plugins: [
        dayGridPlugin,
        timeGridPlugin,
        interactionPlugin,
        listPlugin,
        googleCalendarPlugin,
      ],
      initialView: "dayGridMonth",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
      },
      locale: "es",
      buttonText: {
        today: "Hoy",
        month: "Mes",
        week: "Semana",
        day: "Día",
        list: "Agenda",
      },
      editable: this.editable,
      selectable: true,
      selectMirror: true,
      dayMaxEvents: true,
      weekends: true,
      events: this.localEvents,
      eventClick: this.handleEventClick,
      select: this.handleDateSelect,
      eventDrop: this.handleEventDrop,
      eventResize: this.handleEventResize,
      height: "auto",
      eventColor: "#519DE1",
    };

    // Añadir Google Calendar si está configurado
    if (
      this.enableGoogleCalendar &&
      this.googleCalendarApiKey &&
      this.googleCalendarId
    ) {
      options.googleCalendarApiKey = this.googleCalendarApiKey;
      options.eventSources = [
        {
          googleCalendarId: this.googleCalendarId,
          className: "gcal-event",
          color: "#26A1FF",
        },
      ];
    }

    return options;
  }

  mounted() {
    this.localEvents = [...this.events];
  }

  @Watch("events", { deep: true })
  onEventsChange(newEvents: CalendarEvent[]) {
    this.localEvents = [...newEvents];
    this.refreshCalendar();
  }

  handleEventClick(info: any) {
    this.selectedEvent = {
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      description: info.event.extendedProps.description || "",
      color: info.event.backgroundColor || "",
      category: info.event.extendedProps.category || "",
      allDay: info.event.allDay,
    };
    this.eventDialog = true;
    this.$emit("event-click", this.selectedEvent);
  }

  handleDateSelect(selectInfo: any) {
    this.selectedEvent = {
      title: "",
      start: selectInfo.start,
      end: selectInfo.end,
      allDay: selectInfo.allDay,
    };
    this.eventDialog = true;
    this.$emit("date-select", selectInfo);
  }

  handleEventDrop(info: any) {
    const event: CalendarEvent = {
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
    };
    this.$emit("event-drop", event);
    this.$emit("event-update", event);
  }

  handleEventResize(info: any) {
    const event: CalendarEvent = {
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
    };
    this.$emit("event-resize", event);
    this.$emit("event-update", event);
  }

  openNewEventDialog() {
    this.selectedEvent = {
      title: "",
      start: new Date(),
      allDay: false,
    };
    this.eventDialog = true;
  }

  handleEventSave(event: CalendarEvent) {
    if (event.id) {
      // Update existing event
      const index = this.localEvents.findIndex((e) => e.id === event.id);
      if (index !== -1) {
        this.localEvents.splice(index, 1, event);
      }
      this.$emit("event-update", event);
    } else {
      // Add new event
      event.id = Date.now().toString();
      this.localEvents.push(event);
      this.$emit("event-add", event);
    }
    this.refreshCalendar();
    this.eventDialog = false;
  }

  handleEventDelete(event: CalendarEvent) {
    const index = this.localEvents.findIndex((e) => e.id === event.id);
    if (index !== -1) {
      this.localEvents.splice(index, 1);
    }
    this.$emit("event-delete", event);
    this.refreshCalendar();
    this.eventDialog = false;
  }

  syncGoogleCalendar() {
    this.$emit("google-sync");
    this.refreshCalendar();
  }

  refreshCalendar() {
    const fullCalendar = this.$refs.fullCalendar as any;
    if (fullCalendar) {
      const calendarApi = fullCalendar.getApi();
      if (calendarApi) {
        calendarApi.refetchEvents();
      }
    }
  }

  // Public methods
  public getEvents(): CalendarEvent[] {
    return this.localEvents;
  }

  public addEvent(event: CalendarEvent) {
    this.handleEventSave(event);
  }

  public removeEvent(eventId: string) {
    const event = this.localEvents.find((e) => e.id === eventId);
    if (event) {
      this.handleEventDelete(event);
    }
  }
}
